import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, map } from 'rxjs';
import { CategoryService } from 'src/app/services/category.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { MenuSearchService } from 'src/app/services/menu-search.service';
import { ProductService } from 'src/app/services/product.service';
import { StoplistService } from 'src/app/services/stoplist.service';
import { Drink } from 'src/interfaces/drink.model';
import { Food } from 'src/interfaces/food.model';
import { matchesQuery, queryWords } from 'src/app/utils/menu-search';

type Result = { food?: Food; drink?: Drink };

/**
 * Экран поиска поверх меню: поле, «Отменить», результаты той же сеткой в 2 колонки
 * без категорий, без стоп-листа. Меню под ним не перерисовывается — после закрытия гость на том же
 * месте. Карточка блюда из результатов закрывается обратно в результаты
 */
@Component({
    selector: 'app-menu-search',
    templateUrl: './menu-search.component.html',
    styleUrls: ['./menu-search.component.css'],
    standalone: false
})
export class MenuSearchComponent implements AfterViewInit, OnDestroy {
  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  query = '';
  private query$ = new BehaviorSubject<string>('');
  results$: Observable<Result[]>;
  private destroy$ = new Subject<void>();

  constructor(
    private search: MenuSearchService,
    private localization: LocalizationService,
    productService: ProductService,
    categoryService: CategoryService,
    stoplistService: StoplistService,
  ) {
    this.results$ = combineLatest([
      this.query$,
      productService.foods$,
      productService.drinks$,
      categoryService.categories$,
      stoplistService.foods$,
      stoplistService.drinks$,
    ]).pipe(
      map(([query, foods, drinks, categories, stoplistFoods, stoplistDrinks]) => {
        const words = queryWords(query);
        if (!words.length) return [];
        // Порядок — как в меню: по категориям, внутри — по index
        const order = new Map(categories.map((c) => [c.id, c.index]));
        const items: (Result & { category: string; index: number })[] = [
          // Стоп-лист в результаты не попадает — заказать нельзя
          ...foods
            .filter((food) => !stoplistFoods.includes(food.id))
            .map((food) => ({ food, category: food.category, index: food.index })),
          ...drinks
            .filter((drink) => !stoplistDrinks.includes(drink.id))
            .map((drink) => ({ drink, category: drink.category, index: drink.index })),
        ];
        return items
          .filter((item) => matchesQuery(item.food ?? item.drink!, words, this.localization))
          .sort((a, b) =>
            (order.get(a.category) ?? 0) - (order.get(b.category) ?? 0) || a.index - b.index,
          );
      }),
    );
  }

  ngAfterViewInit(): void {
    this.input?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuery(value: string): void {
    this.query = value;
    this.query$.next(value);
  }

  clear(): void {
    this.onQuery('');
    this.input?.nativeElement.focus();
  }

  close(): void {
    this.search.close();
  }
}
